import { forwardRef, useRef } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const DATE_FORMAT = "DD.MM.YYYY";
const DATETIME_FORMAT = "DD.MM.YYYY HH:mm";

const formatDigits = (value, withTime) => {
  const maxLength = withTime ? 12 : 8;
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, maxLength);

  const dateParts = [digits.slice(0, 2)];

  if (digits.length > 2) dateParts.push(digits.slice(2, 4));
  if (digits.length > 4) dateParts.push(digits.slice(4, 8));

  let result = dateParts.join(".");

  if (withTime && digits.length > 8) {
    result += ` ${digits.slice(8, 10)}`;
  }

  if (withTime && digits.length > 10) {
    result += `:${digits.slice(10, 12)}`;
  }

  return result;
};

const countDigits = (value) => (value.match(/\d/g) || []).length;

const getPositionByDigitCount = (value, digitCount) => {
  if (digitCount <= 0) return 0;

  let currentCount = 0;
  for (let index = 0; index < value.length; index += 1) {
    if (/\d/.test(value[index])) {
      currentCount += 1;
      if (currentCount === digitCount) return index + 1;
    }
  }

  return value.length;
};

const createDigitsInput = (withTime) =>
  forwardRef(({ onChange, ...props }, ref) => {
    const selectionFrameRef = useRef(null);

    const handleChange = (event) => {
      const input = event.target;
      const rawValue = input.value;
      const rawSelectionStart = input.selectionStart ?? rawValue.length;
      const rawSelectionEnd = input.selectionEnd ?? rawSelectionStart;
      const digitsBeforeStart = countDigits(
        rawValue.slice(0, rawSelectionStart),
      );
      const digitsBeforeEnd = countDigits(rawValue.slice(0, rawSelectionEnd));
      const formattedValue = formatDigits(rawValue, withTime);

      input.value = formattedValue;
      onChange?.(event);

      if (selectionFrameRef.current) {
        cancelAnimationFrame(selectionFrameRef.current);
      }

      selectionFrameRef.current = requestAnimationFrame(() => {
        const selectionStart = getPositionByDigitCount(
          formattedValue,
          digitsBeforeStart,
        );
        const selectionEnd = getPositionByDigitCount(
          formattedValue,
          digitsBeforeEnd,
        );

        input.setSelectionRange(selectionStart, selectionEnd);
      });
    };

    return (
      <input
        {...props}
        ref={ref}
        inputMode="numeric"
        onChange={handleChange}
      />
    );
  });

const DateDigitsInput = createDigitsInput(false);
const DateTimeDigitsInput = createDigitsInput(true);

DateDigitsInput.displayName = "DateDigitsInput";
DateTimeDigitsInput.displayName = "DateTimeDigitsInput";

const DATE_COMPONENTS = { input: DateDigitsInput };
const DATETIME_COMPONENTS = { input: DateTimeDigitsInput };

const DigitsDatePicker = ({
  type = "date",
  showTime,
  onBlur,
  onChange,
  ...props
}) => {
  const withTime = type === "moment";

  const handleBlur = (event) => {
    const rawValue = event.target.value?.trim();

    if (withTime && dayjs(rawValue, DATE_FORMAT, true).isValid()) {
      const defaultTimeValue =
        typeof showTime === "object"
          ? showTime.defaultValue || showTime.defaultOpenValue
          : null;
      const defaultTime = dayjs.isDayjs(defaultTimeValue)
        ? defaultTimeValue
        : dayjs("12:00", "HH:mm");
      const dateWithDefaultTime = dayjs(rawValue, DATE_FORMAT)
        .hour(defaultTime.hour())
        .minute(defaultTime.minute())
        .second(0);

      onChange?.(
        dateWithDefaultTime,
        `${rawValue} ${defaultTime.format("HH:mm")}`,
      );
    }

    onBlur?.(event);
  };

  return (
    <DatePicker
      {...props}
      showTime={withTime ? showTime || true : undefined}
      format={withTime ? DATETIME_FORMAT : DATE_FORMAT}
      components={withTime ? DATETIME_COMPONENTS : DATE_COMPONENTS}
      onChange={onChange}
      onBlur={handleBlur}
    />
  );
};

export default DigitsDatePicker;
